import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  ArrowLeft, Brain, Dumbbell, Heart, Flame, Sparkles, Zap,
  TrendingUp, TrendingDown, Moon, Target, Gamepad2, Activity,
  BarChart3, Award
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  PieChart, Pie, Cell
} from "recharts";
import { devError } from "../utils/devLogger";

const METRIC_CONFIG = {
  mood: { label: "Mood", color: "#0ea5e9", icon: Sparkles },
  energy: { label: "Energy", color: "#06b6d4", icon: Zap },
  stress: { label: "Calm", color: "#8b5cf6", icon: Heart },
  focus: { label: "Focus", color: "#6366f1", icon: Brain },
  sleep: { label: "Sleep", color: "#3b82f6", icon: Moon },
  motivation: { label: "Motivation", color: "#f59e0b", icon: Flame },
};

const PIE_COLORS = ["#06b6d4", "#6366f1", "#ec4899", "#f59e0b"];

const ScoreRingLarge = ({ score, label, color, size = 140 }) => {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative flex flex-col items-center">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth="6" />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth="6"
          strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-3xl font-bold text-sky-900" style={{ fontFamily: 'Outfit' }}>{Math.round(score)}</span>
        <span className="text-xs text-slate-400">/100</span>
      </div>
      <span className="text-sm text-sky-700 mt-2 font-medium">{label}</span>
    </div>
  );
};

const ScoreDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/score/detail`);
      setData(res.data);
    } catch (error) {
      devError("Failed to fetch score detail:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  const radarData = data?.avg_metrics ? Object.entries(data.avg_metrics).map(([key, val]) => ({
    metric: METRIC_CONFIG[key]?.label || key,
    value: val,
    fullMark: 10
  })) : [];

  const pieData = data?.activity_distribution ? [
    { name: "Physical", value: data.activity_distribution.physical },
    { name: "Cognitive", value: data.activity_distribution.cognitive },
    { name: "Meditation", value: data.activity_distribution.meditation },
    { name: "Games", value: data.activity_distribution.games },
  ].filter(d => d.value > 0) : [];

  const bestMetric = data?.best_metric ? METRIC_CONFIG[data.best_metric] : null;
  const weakestMetric = data?.weakest_metric ? METRIC_CONFIG[data.weakest_metric] : null;

  return (
    <div className="min-h-screen bg-sky-50" data-testid="score-detail-page">
      <div className="fixed top-0 right-0 w-96 h-96 bg-sky-300/15 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 left-0 w-72 h-72 bg-indigo-300/10 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-sky-600" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-sky-400" strokeWidth={1.5} />
            <span className="text-sm text-sky-700">Body & Mind Score</span>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-5xl mx-auto space-y-6">

          {/* Overall Score Hero */}
          <div className="glass-card p-8" data-testid="overall-score-section">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <ScoreRingLarge score={data?.scores?.overall || 0} label="Overall Score" color="#0ea5e9" size={160} />
              <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { key: "physical", label: "Physical", color: "#06b6d4", icon: Dumbbell },
                  { key: "cognitive", label: "Cognitive", color: "#6366f1", icon: Brain },
                  { key: "consistency", label: "Consistency", color: "#f97316", icon: Flame },
                  { key: "wellness", label: "Wellness", color: "#22c55e", icon: Heart },
                ].map(s => (
                  <div key={s.key} className="text-center">
                    <s.icon className="w-5 h-5 mx-auto mb-2" style={{ color: s.color }} strokeWidth={1.5} />
                    <Progress value={data?.scores?.[s.key] || 0} className="h-2 mb-2" />
                    <p className="text-xl font-semibold text-sky-900" style={{ fontFamily: 'Outfit' }}>
                      {Math.round(data?.scores?.[s.key] || 0)}
                    </p>
                    <p className="text-xs text-slate-500">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Strengths & Weaknesses */}
          {bestMetric && weakestMetric && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="glass-card p-5 flex items-center gap-4 border-l-4 border-green-400" data-testid="strength-card">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-green-600 font-medium uppercase tracking-wider">Your Strength</p>
                  <p className="text-lg font-medium text-sky-900" style={{ fontFamily: 'Outfit' }}>
                    {bestMetric.label}: {data.avg_metrics[data.best_metric]}/10
                  </p>
                </div>
              </div>
              <div className="glass-card p-5 flex items-center gap-4 border-l-4 border-amber-400" data-testid="weakness-card">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Target className="w-6 h-6 text-amber-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-amber-600 font-medium uppercase tracking-wider">Focus Area</p>
                  <p className="text-lg font-medium text-sky-900" style={{ fontFamily: 'Outfit' }}>
                    {weakestMetric.label}: {data.avg_metrics[data.weakest_metric]}/10
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Radar Chart — Wellness Profile */}
          {radarData.length > 0 && (
            <div className="glass-card p-6" data-testid="radar-chart-section">
              <h3 className="text-lg font-medium text-sky-800 mb-4" style={{ fontFamily: 'Outfit' }}>
                Wellness Profile
              </h3>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#e2e8f0" />
                    <PolarAngleAxis dataKey="metric" tick={{ fill: '#64748b', fontSize: 12 }} />
                    <PolarRadiusAxis domain={[0, 10]} tick={false} axisLine={false} />
                    <Radar name="Score" dataKey="value" stroke="#0ea5e9" fill="#0ea5e9" fillOpacity={0.2} strokeWidth={2} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Trend Chart */}
            {data?.daily_trends?.length > 2 && (
              <div className="glass-card p-6" data-testid="trend-chart-section">
                <h3 className="text-lg font-medium text-sky-800 mb-4" style={{ fontFamily: 'Outfit' }}>
                  14-Day Trends
                </h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.daily_trends}>
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} tickFormatter={d => d.slice(5)} />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="mood" stroke="#0ea5e9" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="energy" stroke="#06b6d4" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="focus" stroke="#6366f1" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="sleep" stroke="#3b82f6" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-3 mt-2 justify-center">
                  {[
                    { key: "mood", color: "#0ea5e9" }, { key: "energy", color: "#06b6d4" },
                    { key: "focus", color: "#6366f1" }, { key: "sleep", color: "#3b82f6" }
                  ].map(l => (
                    <div key={l.key} className="flex items-center gap-1.5 text-xs text-slate-500">
                      <div className="w-3 h-0.5 rounded" style={{ backgroundColor: l.color }} />
                      {METRIC_CONFIG[l.key]?.label}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Activity Distribution Pie */}
            {pieData.length > 0 && (
              <div className="glass-card p-6" data-testid="activity-pie-section">
                <h3 className="text-lg font-medium text-sky-800 mb-4" style={{ fontFamily: 'Outfit' }}>
                  Activity Distribution
                </h3>
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value">
                        {pieData.map((_, i) => (
                          <Cell key={`cell-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-3 justify-center">
                  {pieData.map((d, i) => (
                    <div key={d.name} className="flex items-center gap-1.5 text-xs text-slate-500">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      {d.name}: {d.value}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Weekly Score History */}
          {data?.weekly_scores?.length > 1 && (
            <div className="glass-card p-6" data-testid="weekly-score-section">
              <h3 className="text-lg font-medium text-sky-800 mb-4" style={{ fontFamily: 'Outfit' }}>
                Score Progression
              </h3>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.weekly_scores}>
                    <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#94a3b8' }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="score" stroke="#0ea5e9" strokeWidth={3} dot={{ fill: '#0ea5e9', r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="glass-card p-4 text-center" data-testid="stat-streak">
              <Flame className="w-5 h-5 text-orange-500 mx-auto mb-1" strokeWidth={1.5} />
              <p className="text-2xl font-semibold text-sky-900" style={{ fontFamily: 'Outfit' }}>{data?.streak?.current || 0}</p>
              <p className="text-xs text-slate-500">Current Streak</p>
            </div>
            <div className="glass-card p-4 text-center" data-testid="stat-best-streak">
              <Award className="w-5 h-5 text-amber-500 mx-auto mb-1" strokeWidth={1.5} />
              <p className="text-2xl font-semibold text-sky-900" style={{ fontFamily: 'Outfit' }}>{data?.streak?.highest || 0}</p>
              <p className="text-xs text-slate-500">Best Streak</p>
            </div>
            <div className="glass-card p-4 text-center" data-testid="stat-days">
              <BarChart3 className="w-5 h-5 text-sky-500 mx-auto mb-1" strokeWidth={1.5} />
              <p className="text-2xl font-semibold text-sky-900" style={{ fontFamily: 'Outfit' }}>{data?.total_days_tracked || 0}</p>
              <p className="text-xs text-slate-500">Days Tracked</p>
            </div>
            <div className="glass-card p-4 text-center" data-testid="stat-activities">
              <Gamepad2 className="w-5 h-5 text-indigo-500 mx-auto mb-1" strokeWidth={1.5} />
              <p className="text-2xl font-semibold text-sky-900" style={{ fontFamily: 'Outfit' }}>
                {(data?.activity_distribution?.physical || 0) + (data?.activity_distribution?.cognitive || 0)}
              </p>
              <p className="text-xs text-slate-500">Total Activities</p>
            </div>
          </div>

          {/* Metric Details */}
          {data?.avg_metrics && Object.keys(data.avg_metrics).length > 0 && (
            <div className="glass-card p-6" data-testid="metric-details-section">
              <h3 className="text-lg font-medium text-sky-800 mb-4" style={{ fontFamily: 'Outfit' }}>
                Metric Averages (Last 7 Days)
              </h3>
              <div className="space-y-3">
                {Object.entries(data.avg_metrics).map(([key, val]) => {
                  const config = METRIC_CONFIG[key];
                  if (!config) return null;
                  const MetricIcon = config.icon;
                  return (
                    <div key={key} className="flex items-center gap-3">
                      <MetricIcon className="w-4 h-4 flex-shrink-0" style={{ color: config.color }} strokeWidth={1.5} />
                      <span className="text-sm text-sky-700 w-20">{config.label}</span>
                      <div className="flex-1">
                        <Progress value={val * 10} className="h-2.5" />
                      </div>
                      <span className="text-sm font-semibold w-10 text-right" style={{ color: config.color }}>{val}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty State */}
          {(!data?.avg_metrics || Object.keys(data.avg_metrics).length === 0) && (
            <div className="glass-card p-12 text-center">
              <Activity className="w-12 h-12 text-sky-300 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-lg font-medium text-sky-800 mb-2" style={{ fontFamily: 'Outfit' }}>
                Start tracking to see your score
              </h3>
              <p className="text-sm text-slate-500 mb-6">
                Complete your daily check-in and exercises to build your Body & Mind profile.
              </p>
              <Button onClick={() => navigate('/questionnaire')} className="glow-button" data-testid="start-tracking-btn">
                Start Check-in
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ScoreDetail;
